import { LocalDate } from '@js-joda/core';
import { useCallback } from 'react';
import { SubmitHandler } from 'react-hook-form';
import styled from 'styled-components';

import { Country, Postcode } from '@dreryk/address-common-api';
import { GuardianExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';
import { EmailAddress } from '@dreryk/email-address-common-api';
import {
   NationalIdentificationNumber,
   NationalIdentificationNumberCountry,
   Passport,
   Pesel,
} from '@dreryk/personal-external-identifier-common-api';
import { PhoneNumber } from '@dreryk/phone-number-common-api';
import { Gender } from '@dreryk/egabinet-patient-frontend-api';

import { FormContextValues, TranslatableText, useFormController } from '@dreryk/react-common';
import { Modal, ModalWidth } from '@dreryk/react-components';

import { guardiansTranslationKeys } from '../../../translations';
import {
   createGuardianExternalIdentifierTypeVisitor,
   GuardianExternalIdentifierType,
} from '../../PersonForm/GuardianExternalIdentifier/GuardianExternalIdentifierTypeField';
import { GuardianExternalIdentifierValue } from '../../PersonForm/GuardianExternalIdentifier/GuardianExternalIdentifierValueField';

import { RequiredAddressDataForStatutoryRepresentativeStatus } from './RequiredAddressDataForStatutoryRepresentativeStatus';
import { ContactDetailsSection } from './sections/ContactDetailsSection';
import { PersonalDataSection } from './sections/PersonalDataSection';

const StyledGuardianDetailsModalContent = styled.div`
   display: flex;
   flex-direction: column;
   gap: 24px;
`;
StyledGuardianDetailsModalContent.displayName = 'StyledGuardianDetailsModalContent';

const StyledGuardianDetailsFormContent = styled.div`
   display: flex;
   flex-direction: column;
   gap: 40px;
`;
StyledGuardianDetailsFormContent.displayName = 'StyledGuardianDetailsFormContent';

export type GuardianDetailsFormBean = {
   firstName: string | null;
   middleName: string | null;
   lastName: string | null;
   guardianExternalIdentifierType: GuardianExternalIdentifierType;
   guardianExternalIdentifierValue: GuardianExternalIdentifierValue;
   passportCountry: Country | null;
   passportValidTo: LocalDate | null;
   nationalIdentificationNumberCountry: NationalIdentificationNumberCountry | null;
   phoneNumber: PhoneNumber | null;
   emailAddress: EmailAddress | null;
   gender: Gender | null;
   flat: string | null;
   country: Country;
   town: string | null;
   postcode: Postcode | null;
   street: string | null;
   house: string | null;
};

export type GuardianDetailsFormController = FormContextValues<GuardianDetailsFormBean>;

export type OnGuardianDetailsFormSubmit = SubmitHandler<GuardianDetailsFormBean>;

export const createGuardianExternalIdentifier = (guardian: GuardianDetailsFormBean): GuardianExternalIdentifier => {
   const guardianExternalIdentifierTypeVisitor = createGuardianExternalIdentifierTypeVisitor(
      guardian.guardianExternalIdentifierType
   );

   return GuardianExternalIdentifier.of(
      guardianExternalIdentifierTypeVisitor<Pesel | Passport | NationalIdentificationNumber>({
         visitPesel: () => Pesel.of(guardian.guardianExternalIdentifierValue!),
         visitPassport: () =>
            Passport.of(
               guardian.passportCountry!,
               guardian.guardianExternalIdentifierValue!,
               guardian.passportValidTo!
            ),
         visitNationalIdentificationNumber: () =>
            NationalIdentificationNumber.of(
               guardian.nationalIdentificationNumberCountry!,
               guardian.guardianExternalIdentifierValue!
            ),
      })
   );
};

type GuardianDetailsModalProps = {
   readonly onSubmit: OnGuardianDetailsFormSubmit;
   readonly onToggleVisibility: VoidFunction;
   readonly statutoryRepresentative: boolean;
   readonly initialValues?: GuardianDetailsFormBean;
   readonly headerText: TranslatableText;
};

export const GuardianDetailsModal = (props: GuardianDetailsModalProps): JSX.Element => {
   const { onSubmit, onToggleVisibility } = props;
   const { handleSubmit, register, watch, setFocus, setValue } = useFormController<GuardianDetailsFormBean>(
      props.initialValues ?? {
         guardianExternalIdentifierType: 'Pesel',
         guardianExternalIdentifierValue: null,
         passportCountry: null,
         nationalIdentificationNumberCountry: null,
         passportValidTo: null,
         firstName: null,
         middleName: null,
         lastName: null,
         country: Country.DEFAULT,
         town: null,
         postcode: null,
         street: null,
         house: null,
         phoneNumber: null,
         emailAddress: null,
         flat: null,
      }
   );

   const handleFormSubmit = useCallback<OnGuardianDetailsFormSubmit>(
      (formData) => {
         onSubmit(formData);
         onToggleVisibility();
      },
      [onSubmit, onToggleVisibility]
   );

   return (
      <Modal
         title={props.headerText}
         onCancel={onToggleVisibility}
         onSubmit={handleSubmit(handleFormSubmit)}
         width={ModalWidth.HUGE}
         confirmationButtonLabel={guardiansTranslationKeys.save}
         wrapInForm
         testId={'guardianDetails'}
      >
         <StyledGuardianDetailsModalContent>
            <RequiredAddressDataForStatutoryRepresentativeStatus addressRequired={props.statutoryRepresentative} />
            <StyledGuardianDetailsFormContent>
               <PersonalDataSection
                  register={register}
                  setFocus={setFocus}
                  setValue={setValue}
                  watch={watch}
                  statutoryRepresentative={props.statutoryRepresentative}
               />
               <ContactDetailsSection
                  register={register}
                  watch={watch}
                  statutoryRepresentative={props.statutoryRepresentative}
               />
            </StyledGuardianDetailsFormContent>
         </StyledGuardianDetailsModalContent>
      </Modal>
   );
};
